/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import configuration */
import {CSV_SENSOR_MUENSTER, CSV_SENSOR_NEUTRAUBLING} from '@/config/csvSensors';

/* Import libraries */
import { csvToDeviceSensorMetadata } from '@/lib/sensors/csv/csvToDeviceSensorMetadata';
import { csvToSensorMetadata } from '@/lib/sensors/csv/csvToSensorMetadata';

/* Import types */
import type { DeviceSensorConfig, DeviceSensorSystemGroup } from '@/types/deviceSensor';

const PV_SENSOR_TYPES = [
    'pred_new',
    'pred_recent',
    'ratio_30',
    'new_30',
    'recent_30',
    'ref_30',
    'warning',
    'error',
    'loss_pct',
    'lost_30',
    'lost_1',
    'dtc',
    'loss.7_abs',
    'loss.30_abs',
    'loss.180_abs',
    'loss.360_abs',
    'loss.7_rel',
    'loss.30_rel',
    'loss.180_rel',
    'loss.360_rel',
    'loss.7_hours',
    'loss.30_hours',
    'loss.180_hours',
    'loss.360_hours',
    'loss.dtc',
    'redispatch.active',
    'redispatch.dtc',
    'redispatch.power_lost',
    'redispatch.energy_30',
    'outage.active',
    'outage.dtc',
    'outage.evidance',
    'outage.fault_age',
    'outage.healthy_age',
];

function mergeCustomerDeviceSensorConfigs(configs: DeviceSensorConfig[]): DeviceSensorConfig {
    const sensorGroups = Array.from(new Set(configs.flatMap((config) => config.sensorGroups)));

    return {
        baseSensorTypes: Array.from(new Set(configs.flatMap((config) => config.baseSensorTypes))),
        deviceMetadata: Object.assign({}, ...configs.map((config) => config.deviceMetadata)),
        sensorMetadata: Object.assign({}, ...configs.map((config) => config.sensorMetadata)),
        sensorGroups,
        sensorTypes: sensorGroups.reduce<Record<DeviceSensorSystemGroup, string[]>>((sensorTypes, group) => {
            sensorTypes[group] = Array.from(new Set(configs.flatMap((config) => config.sensorTypes[group])));

            return sensorTypes;
        }, {
            DTC: [],
            BL: [],
            HE: [],
            PV: [],
            WL: [],
        }),
        translationTable: configs.flatMap((config) => {
            if (config.translationTable.length > 0) {
                return config.translationTable;
            }

            return Object.keys(config.deviceMetadata).map((deviceName) => ({
                real: deviceName,
                virtual: deviceName,
            }));
        }),
    };
}

function createMuensterDeviceSensorConfig(): DeviceSensorConfig {
    return {
    baseSensorTypes: [],
    deviceMetadata: csvToDeviceSensorMetadata(CSV_SENSOR_MUENSTER),
    sensorMetadata: csvToSensorMetadata(CSV_SENSOR_MUENSTER, {
        customer: 'Münster',
        includeDisabled: true,
    }),
    sensorGroups: ['BL', 'HE', 'PV', 'WL'], /* There is no DTC for "Mue" Sensors/Devices */
    sensorTypes: {
        DTC: [],
        BL: [
            'CostDay',
            'CostOverall',
            'Deviation',
            'DeviationDay',
            'DeviationPercent',
            'Min',
            'MinOverall',
            'Severity',
            'Threshold',
            'ToleranceCounter',
        ],
        HE: [
            'BaseLoad',
            'CutoffTemperature',
            'Energy',
            'EnergyAmountTrain',
            'OutsideTemperature',
            'SavedCosts',
            'SavedEnergy',
            'WeekendReductionPercent',
            'WeekendReductionValue',
        ],
        PV: [...PV_SENSOR_TYPES],
        WL: [
            'V_loss',
            'qv_loss_1d',
            'qv_loss_7d',
            'dtc_7d',
            'dtc',
        ],
    },
    translationTable: [], /* All devices don't have virtual devices (real == virtual) */
    };
}

function createNeutraublingDeviceSensorConfig(): DeviceSensorConfig {
    const csvSensorData = CSV_SENSOR_NEUTRAUBLING ?? '';

    return {
    baseSensorTypes: ['P', 'W_15m', 'W_1d', 'W_1h'],
    deviceMetadata: csvToDeviceSensorMetadata(csvSensorData),
    sensorMetadata: csvToSensorMetadata(csvSensorData, {
        customer: 'Neutraubling',
        includeDisabled: true,
    }),
    sensorGroups: ['DTC', 'BL', 'HE', 'PV', 'WL'],
    sensorTypes: {
        DTC: [],
        BL: [
            'CostDay',
            'CostOverall',
            'Deviation',
            'DeviationDay',
            'DeviationPercent',
            'Min',
            'MinOverall',
            'Severity',
            'Threshold',
            'ToleranceCounter',
        ],
        HE: [
            'BaseLoad',
            'CutoffTemperature',
            'Energy',
            'EnergyAmountTrain',
            'OutsideTemperature',
            'SavedCosts',
            'SavedEnergy',
            'WeekendReductionPercent',
            'WeekendReductionValue',
        ],
        PV: [...PV_SENSOR_TYPES],
        WL: [
            'V_loss',
            'qv_loss_1d',
            'qv_loss_7d',
            'dtc_7d',
            'dtc',
        ],
    },
    translationTable: [
        { virtual: 'GN.Z01', real: 'N.Z01' },
        { virtual: 'GN.Z02', real: 'N.Z02' },
        { virtual: 'GN.Z03', real: 'N.Z03' },
        { virtual: 'GN.Z04', real: 'N.Z04' },
        { virtual: 'GN.Z05', real: 'BA1.Z05' },
        { virtual: 'GN.Z06', real: 'BA1.Z06' },
        { virtual: 'GN.Z07', real: 'BA1.Z07' },
        { virtual: 'GN.Z08', real: 'BA1.Z08' },
        { virtual: 'GN.Z13', real: 'BA1.Z13' },
        { virtual: 'GN.Z14', real: 'BA1.Z14' },
        { virtual: 'GN.Z15', real: 'BA1.Z15' },
        { virtual: 'GN.Z17', real: 'BA1.Z17' },
        { virtual: 'GN.Z18', real: 'BA1.Z18' },
        { virtual: 'GN.Z19', real: 'BA1.Z19' },
        { virtual: 'GN.Z20', real: 'BA1.Z20' },
        { virtual: 'GN.Z21', real: 'BA1.Z21' },
        { virtual: 'GN.Z23', real: 'BA1.Z23' },
        { virtual: 'GN.Z24', real: 'BA1.Z24' },
        { virtual: 'GN.Z25', real: 'BA1.Z25' },
        { virtual: 'GN.Z26', real: 'BA2.Z26' },
        { virtual: 'GN.Z27', real: 'BA2.Z27' },
        { virtual: 'GN.Z28', real: 'BA2.Z28' },
        { virtual: 'GN.Z32', real: 'BA2.Z32' },
        { virtual: 'GN.Z33', real: 'BA2.Z33' },
        { virtual: 'GN.Z34', real: 'BA2.Z34' },
        { virtual: 'GN.Z35', real: 'BA2.Z35' },
        { virtual: 'GN.Z36', real: 'BA2.Z36' },
        { virtual: 'GN.Z37', real: 'BA2.Z37' },
        { virtual: 'GN.Z38', real: 'BA2.Z38' },
        { virtual: 'GN.Z39', real: 'BA3.Z39' },
        { virtual: 'GN.Z40', real: 'BA3.Z40' },
        { virtual: 'GN.Z41', real: 'BA3.Z41' },
        { virtual: 'GN.Z42', real: 'BA3.Z42' },
        { virtual: 'GN.Z43', real: 'BA3.Z43' },
        { virtual: 'GN.Z44', real: 'BA3.Z44' },
        { virtual: 'GN.Z45', real: 'BA3.Z45' },
        { virtual: 'GN.Z46', real: 'BA3.Z46' },
    ],
    };
}

export let MUENSTER_DEVICE_SENSOR_CONFIG = createMuensterDeviceSensorConfig();
export let NEUTRAUBLING_DEVICE_SENSOR_CONFIG = createNeutraublingDeviceSensorConfig();
export let CUSTOMER_DEVICE_SENSOR_CONFIGS = [
    MUENSTER_DEVICE_SENSOR_CONFIG,
    ...(CSV_SENSOR_NEUTRAUBLING === null ? [] : [NEUTRAUBLING_DEVICE_SENSOR_CONFIG]),
];

export let OVERVIEW_DEVICE_SENSOR_CONFIG = mergeCustomerDeviceSensorConfigs(CUSTOMER_DEVICE_SENSOR_CONFIGS);

/** Rebuilds CSV-derived device metadata after the active user changes. */
export function refreshDeviceSensorConfigs(): void {
    MUENSTER_DEVICE_SENSOR_CONFIG = createMuensterDeviceSensorConfig();
    NEUTRAUBLING_DEVICE_SENSOR_CONFIG = createNeutraublingDeviceSensorConfig();
    CUSTOMER_DEVICE_SENSOR_CONFIGS = [
        MUENSTER_DEVICE_SENSOR_CONFIG,
        ...(CSV_SENSOR_NEUTRAUBLING === null ? [] : [NEUTRAUBLING_DEVICE_SENSOR_CONFIG]),
    ];
    OVERVIEW_DEVICE_SENSOR_CONFIG = mergeCustomerDeviceSensorConfigs(CUSTOMER_DEVICE_SENSOR_CONFIGS);
}
